/**
 * The processing that happens after photographs land.
 *
 * Uploading was the only thing the web client did. Everything that makes an
 * archive an archive — who is in each picture, when it was taken, what it
 * shows — happened only in the desktop app, so a folder added here arrived as
 * a wall of untitled rectangles and stayed that way.
 *
 * ## Where each stage runs, and why
 *
 * **Faces run here, in the tab.** Not because it is convenient but because it
 * is the only place they are allowed to: a 512-dimensional face template is a
 * regulated biometric identifier and the server must never hold one. So the
 * detector and the embedder are fetched into the browser, the vectors are
 * matched against exemplars the browser rebuilt from crops, and what reaches
 * the server is a name and a rectangle. Nothing else would be lawful without
 * consent from every person in every photograph, which for a shoebox of 1950s
 * prints is not obtainable.
 *
 * **Descriptions and the date check run on the server**, because they need a
 * vision-language model that is far too big to ship to a tab, and because a
 * caption is not a biometric identifier. They are queued, not awaited.
 *
 * **Page splitting and colour work stay on the desktop.** Both need models and
 * decode paths a browser does not have, and both are destructive-ish enough
 * that doing them in the background without being watched is the wrong call.
 *
 * ## Why it is a queue and not a loop
 *
 * A folder import is thousands of photographs and each one is a decode, a
 * detector pass and an embed per face. Run flat out it makes the tab
 * unresponsive and the browser kills it. So work is done one photograph at a
 * time with a yield between, it survives being interrupted, and it picks up
 * where it stopped — the server records what has been processed, so closing
 * the tab loses nothing but time.
 */

import { api } from "./api";
import { blake3 } from "hash-wasm";
import { context2d, imageData, surface, toBlob } from "./canvas";
import { store } from "./state";
import { count } from "./format";
import type { Rejection } from "./types";

/** What the runner is doing, for the caller to show. */
export interface Progress {
  stage: "loading models" | "faces" | "queued" | "done" | "idle";
  done: number;
  total: number;
  /** Faces found so far, and how many were put to a name. */
  faces: number;
  named: number;
  /** New people made by grouping faces nobody has named. */
  groups: number;
  /**
   * Photographs the pass could not read at all.
   *
   * Reported rather than swallowed. These are not stamped, so they come back
   * on the next run — which is right for a transient failure and means a
   * permanently unreadable file would otherwise sit in the queue forever,
   * invisible, making the pass look like it never finishes. One line in the
   * summary is the difference between "there is a file here I cannot read" and
   * a Process button that quietly never gets to the end.
   */
  unreadable: number;
  note?: string;
}

export interface RunOptions {
  onProgress?: (p: Progress) => void;
  /** Stop early. Checked between photographs, so it is never abrupt. */
  cancelled?: () => boolean;
  /**
   * Whether somebody named, merged, placed or rejected a face since the pass
   * began. Asked once the detector is done: a yes means the exemplars and
   * rejections loaded at the start are stale, and the faces are placed again
   * against fresh ones before anything is written.
   */
  evidenceChanged?: () => boolean;
}

/** Fractional box plus a name, which is all the server ever sees. */
interface FaceLabel {
  id: string;
  bbox: [number, number, number, number];
  identityId: string | null;
  detScore: number;
  quality: number;
  similarity?: number;
  state: "auto" | "suggested";
}

type Matching = typeof import("@heirloom/matching/src/match");
type Detecting = typeof import("@heirloom/matching/src/detect");
type Embedding = typeof import("@heirloom/matching/src/embed");

// Awaited return types rather than `InstanceType`: both classes have private
// constructors, deliberately — they are only reachable through `load`, which
// is what stops one being built without its model.
let loaded: {
  match: Matching;
  detect: Detecting;
  embed: Embedding;
  detector: Awaited<ReturnType<Detecting["YuNet"]["load"]>>;
  embedder: Awaited<ReturnType<Embedding["Embedder"]["load"]>>;
} | null = null;

/**
 * Fetch the models once per page.
 *
 * ONNX Runtime Web comes from a CDN by script tag rather than a bundle: it
 * ships its own WebAssembly and wants to fetch it by a path it decides, and
 * bundling it means fighting that for no gain.
 */
async function models(note: (s: string) => void): Promise<NonNullable<typeof loaded>> {
  if (loaded) return loaded;
  note("loading the face models");

  if (!("ort" in globalThis)) {
    await new Promise<void>((res, rej) => {
      const tag = document.createElement("script");
      tag.src = "https://cdnjs.cloudflare.com/ajax/libs/onnxruntime-web/1.20.1/ort.min.js";
      tag.onload = () => res();
      tag.onerror = () => rej(new Error("could not load the inference runtime"));
      document.head.appendChild(tag);
    });
    const ort = (globalThis as { ort?: { env: { wasm: { wasmPaths: string } } } }).ort;
    if (ort) {
      ort.env.wasm.wasmPaths =
        "https://cdnjs.cloudflare.com/ajax/libs/onnxruntime-web/1.20.1/";
    }
  }

  const [match, detect, embed] = await Promise.all([
    import("@heirloom/matching/src/match"),
    import("@heirloom/matching/src/detect"),
    import("@heirloom/matching/src/embed"),
  ]);
  const detector = await detect.YuNet.load("/api/model/yunet.onnx");
  note("loading the face recogniser — 63 MB, once");
  const embedder = await embed.Embedder.load({ modelUrl: "/api/model/arcface_int8.onnx" });

  loaded = { match, detect, embed, detector, embedder };
  return loaded;
}

/**
 * The face embedder, loaded once and shared.
 *
 * The same-person comparison used to load its own copy — a second 63 MB
 * download, and one that failed outright unless a face pass had already put
 * the inference runtime on the page.
 */
export async function faceEmbedder(): Promise<NonNullable<typeof loaded>["embedder"]> {
  return (await models(() => {})).embedder;
}

/**
 * Exemplar vectors by crop key, for the life of the tab.
 *
 * Every pass re-embedded every crop of every person before it looked at a
 * single photograph, which made re-running after each name a minute of
 * nothing. A crop is content-addressed, so its vector never changes. Held in
 * memory only — a face template never leaves this tab.
 */
const cropVectors = new Map<string, Float32Array>();

export async function cropVector(cropKey: string): Promise<Float32Array | null> {
  const known = cropVectors.get(cropKey);
  if (known) return known;
  const m = await models(() => {});
  const res = await fetch(`/api/blob/${cropKey.split("/").pop()}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  const vector = await m.embedder.embed(await m.embed.decodeCrop(await res.blob()));
  cropVectors.set(cropKey, vector);
  return vector;
}

type Exemplar = import("@heirloom/matching/src/match").Exemplar;

/**
 * What a face is matched against: every person's crops, and every refusal.
 *
 * Loaded before anything is identified, and again if a correction lands while
 * the pass is running.
 */
async function evidence(): Promise<{ refused: Rejection[]; exemplars: Exemplar[] }> {
  // Matches somebody has already refused. Without them the pass finds the
  // same similarity it found last time and re-proposes a face that was
  // rejected — the correction would last exactly until the next run, which is
  // worse than not offering one.
  //
  // Keyed by region rather than by face id: `putFaces` gives the same physical
  // face a new id every pass, so an id would match nothing here.
  let refused: Rejection[] = [];
  try {
    refused = await api.rejections(store.libraryId);
  } catch {
    // Not fatal. A pass that cannot read the rejections may re-propose a
    // corrected face, which is annoying; refusing to run at all is worse.
  }

  // Exemplars this library already has, re-embedded locally from crops. This
  // is what turns a detection into a name rather than a numbered stranger.
  const exemplars: Exemplar[] = [];
  try {
    const people = await api.people(store.libraryId);
    for (const person of people) {
      const page = await api.person(store.libraryId, person.identityId);
      for (const crop of page.crops.slice(0, 8)) {
        try {
          const vector = await cropVector(crop.cropKey);
          if (!vector) continue;
          exemplars.push({
            identityId: person.identityId,
            vector,
            source: store.libraryId,
            quality: crop.quality,
          });
        } catch {
          // One unreadable crop is one exemplar fewer, not a failed pass.
        }
      }
    }
  } catch {
    // No exemplars means every face is a stranger, which is the correct
    // first-run behaviour rather than a failure.
  }
  return { refused, exemplars };
}

/**
 * Who this face has already been ruled out as.
 *
 * A detector run again on the same pixels does not put the box in exactly the
 * same place — a percent or two of drift is normal — so this matches by
 * overlap rather than equality. Intersection over union, at a threshold loose
 * enough to survive that drift and tight enough not to catch the face
 * standing next to it.
 */
const REJECT_IOU = 0.4;

export function refusedHere(
  refused: readonly Rejection[],
  assetId: string,
  box: [number, number, number, number],
): Set<string> {
  const out = new Set<string>();
  for (const r of refused) {
    if (r.assetId !== assetId) continue;
    if (iou(r.bbox, box) >= REJECT_IOU) out.add(r.identityId);
  }
  return out;
}

function iou(
  a: readonly [number, number, number, number],
  b: readonly [number, number, number, number],
): number {
  const x = Math.max(a[0], b[0]);
  const y = Math.max(a[1], b[1]);
  const r = Math.min(a[0] + a[2], b[0] + b[2]);
  const t = Math.min(a[1] + a[3], b[1] + b[3]);
  if (r <= x || t <= y) return 0;
  const overlap = (r - x) * (t - y);
  const union = a[2] * a[3] + b[2] * b[3] - overlap;
  return union > 0 ? overlap / union : 0;
}

/** The aligned face as a JPEG: what gets stored and shown. */
async function encodeCrop(rgb: Uint8Array): Promise<Blob | null> {
  const size = 112;
  const s = surface(size, size);
  const ctx = context2d(s);
  if (!ctx) return null;
  const img = ctx.createImageData(size, size);
  for (let i = 0, j = 0; i < rgb.length; i += 3, j += 4) {
    img.data[j] = rgb[i]!;
    img.data[j + 1] = rgb[i + 1]!;
    img.data[j + 2] = rgb[i + 2]!;
    img.data[j + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return toBlob(s, "image/jpeg", 0.88);
}

/** Decode to RGBA, preferring the small rendition the upload already made. */
async function pixels(hash: string): Promise<ImageData | null> {
  try {
    const res = await fetch(`/api/blob/${hash}`, { credentials: "include" });
    if (!res.ok) return null;
    const bmp = await createImageBitmap(await res.blob());
    const s = surface(bmp.width, bmp.height);
    const ctx = context2d(s);
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0);
    const out = imageData(s, bmp.width, bmp.height);
    bmp.close();
    return out;
  } catch {
    return null;
  }
}

/**
 * Find and name the people in everything that has not been looked at.
 *
 * Runs against the proxy rather than the original, which is a real trade: a
 * 1280 px rendition is enough for a face that fills a decent part of the frame
 * and not enough for a face in the back row of a group photograph. The
 * alternative is downloading a 1.5 MB original per photograph, which for a
 * folder of two thousand is three gigabytes through a tab. Measured in the Rust
 * pipeline, dropping working width from 1600 to 640 was what turned 1,849
 * detections into noise — 1280 sits above that, but a second pass at full size
 * for anything that found nobody is the obvious next move.
 */
export async function processFaces(opts: RunOptions = {}): Promise<Progress> {
  const p: Progress = {
    stage: "idle", done: 0, total: 0, faces: 0, named: 0, groups: 0,
    unreadable: 0,
  };
  const say = (): void => opts.onProgress?.({ ...p });
  const note = (s: string): void => {
    p.stage = "loading models";
    p.note = s;
    say();
  };

  // Only what nobody has looked at. Re-running is therefore cheap and safe,
  // and an interrupted run resumes rather than starting again.
  // `facesPending`, not `unnamed`. The two look interchangeable and are not:
  // `unnamed` asks whether any face here has an identity, so a photograph
  // whose faces were grouped into an unnamed person answers yes and was
  // skipped by every subsequent run. Photographs the pass had already found
  // people in were unreachable, which is why the crops it never uploaded could
  // not be repaired by pressing Process again.
  const pending = await api.cards(
    store.libraryId,
    { facesPending: true },
    { limit: 500 },
  );
  p.total = pending.items.length;
  if (!p.total) {
    p.stage = "done";
    say();
    return p;
  }

  const m = await models(note);
  p.stage = "faces";
  p.note = undefined;
  say();

  let known = await evidence();

  /**
   * Who one face is, given what the archive knows.
   *
   * Anyone this face has already been taken off is out of the running.
   * Filtering the exemplars rather than discarding the match afterwards, so
   * the *second* best candidate can still win — a face wrongly given to Mary is
   * often right about her sister, and rejecting it should hand it to the
   * sister rather than leave it a stranger.
   */
  const place = (
    assetId: string,
    bbox: [number, number, number, number],
    vector: Float32Array,
  ) => {
    const barred = refusedHere(known.refused, assetId, bbox);
    const allowed = barred.size
      ? known.exemplars.filter((ex) => !barred.has(ex.identityId))
      : known.exemplars;
    return m.match.identify(vector, allowed, { localSource: store.libraryId });
  };

  // Detect and embed everything first, then group. Grouping has to see the
  // whole batch: the same person appears twice in one photograph almost never,
  // so per-photograph clustering finds nothing and every face in the archive
  // stays a separate stranger — which is exactly what the first run of this
  // did, 25 faces and no people at all.
  interface Found {
    assetId: string;
    id: string;
    bbox: [number, number, number, number];
    score: number;
    vector: Float32Array;
    identityId: string | null;
    similarity?: number;
    /** JPEG of the aligned face, for the thumbnail and as an exemplar. */
    crop?: Blob;
  }
  const found: Found[] = [];

  // Photographs the detector actually ran on, faces or not.
  //
  // Kept separately from `found` because the two differ in the case that
  // matters: a photograph holding nobody produces no entry in `found`, so
  // writing back only what `found` mentions leaves it unstamped and it is
  // offered again on every run for the rest of the library's life. A decode
  // failure is deliberately *not* in here — that one should be retried.
  const examined = new Set<string>();

  for (const card of pending.items) {
    if (opts.cancelled?.()) break;
    p.done++;
    p.note = card.fileName;
    say();

    const source = card.proxyKey?.split("/").pop() ?? card.contentHash;
    const img = await pixels(source);
    if (!img) {
      p.unreadable++;
      console.warn(`face pass: could not decode ${card.fileName} (${source})`);
      continue;
    }

    let faces;
    try {
      faces = await m.detector.detect(img.data, img.width, img.height);
    } catch (e) {
      p.unreadable++;
      // Named, because "the detector refused this image" and "this file is not
      // an image" are different problems and the message is the only thing
      // that separates them.
      console.warn(`face pass: detector failed on ${card.fileName}:`, e);
      continue;
    }
    examined.add(card.id);
    for (const f of m.detect.bigEnough(faces, img.width, img.height)) {
      const crop = m.detect.cropFor(img.data, img.width, img.height, f);
      let vector: Float32Array;
      try {
        vector = await m.embedder.embed(crop);
      } catch {
        continue;
      }
      // Encoded now, while the pixels are to hand. 112x112 at quality 88 is
      // about 4 kB — the same crop the desktop stores.
      const cropJpeg = await encodeCrop(crop);
      const bbox: [number, number, number, number] = [f.x, f.y, f.w, f.h];
      const hit = place(card.id, bbox, vector);
      p.faces++;
      if (hit?.certain) p.named++;
      found.push({
        assetId: card.id,
        id: crypto.randomUUID(),
        bbox,
        score: f.score,
        vector,
        identityId: hit?.certain ? hit.identityId : null,
        similarity: hit?.similarity,
        crop: cropJpeg ?? undefined,
      });
    }
    say();
    // Let the tab breathe. Without this a folder of two thousand locks the UI
    // and the browser eventually kills the page.
    await new Promise((r) => setTimeout(r, 0));
  }

  // Somebody corrected the archive while the detector was running — the usual
  // case, since naming people is what someone does while a pass works. The
  // vectors are still in memory, so placing every face again against fresh
  // evidence costs no decode; skipping it would write results from before the
  // correction and stamp those photographs as done.
  if (found.length && opts.evidenceChanged?.()) {
    p.note = "catching up with your changes";
    say();
    known = await evidence();
    p.named = 0;
    for (const f of found) {
      const hit = place(f.assetId, f.bbox, f.vector);
      f.identityId = hit?.certain ? hit.identityId : null;
      f.similarity = hit?.similarity;
      if (hit?.certain) p.named++;
    }
  }

  // Everyone the exemplars did not account for, grouped into people.
  //
  // A group of one is left alone rather than turned into a person: an archive
  // where every passer-by in the background becomes their own entry is a list
  // nobody will ever work through.
  const strangers = found.filter((f) => !f.identityId);
  if (strangers.length) {
    p.note = `grouping ${count(strangers.length, "face")}`;
    say();
    const groups = m.match.groupUnknown(
      strangers.map((f) => ({ id: f.id, vector: f.vector })),
    );
    for (const group of groups) {
      if (group.length < 2) continue;
      const identityId = crypto.randomUUID();
      try {
        await api.createIdentity(store.libraryId, identityId);
      } catch {
        continue;
      }
      for (const faceId of group) {
        const f = strangers.find((x) => x.id === faceId);
        if (f) f.identityId = identityId;
      }
      p.groups++;
      say();
    }
  }

  // A face for each person, so they are recognisable.
  //
  // This was missing, and its absence made the People screen useless: without
  // a crop there is nothing to show but the first letter of a name, so a
  // hundred groups looked like a hundred identical lettered circles and there
  // was no way to tell who any of them were — or to name them, which is the
  // one thing that screen is for.
  //
  // The best-scoring face per person, capped low. These are exemplars as well
  // as thumbnails, and the cap exists because they are matched against on
  // every later pass.
  const byPerson = new Map<string, Found[]>();
  for (const f of found) {
    if (!f.identityId) continue;
    const list = byPerson.get(f.identityId) ?? [];
    list.push(f);
    byPerson.set(f.identityId, list);
  }
  //
  // Only up to that cap. Every pass used to upload four more for everybody it
  // recognised — the same faces again under new ids — and each upload offers
  // the archive's unplaced faces back for another pass. Once passes re-run
  // after every name, that is a pass that keeps scheduling itself.
  const CROPS_PER_PERSON = 4;
  const have = new Map<string, number>();
  for (const ex of known.exemplars) {
    have.set(ex.identityId, (have.get(ex.identityId) ?? 0) + 1);
  }
  for (const [identityId, faces] of byPerson) {
    const room = CROPS_PER_PERSON - (have.get(identityId) ?? 0);
    if (room <= 0) continue;
    const best = [...faces].sort((a, b) => b.score - a.score).slice(0, room);
    for (const f of best) {
      if (!f.crop) continue;
      try {
        const cropHash = await blake3(new Uint8Array(await f.crop.arrayBuffer()));
        const up = await fetch(`/api/lib/${store.libraryId}/upload/${cropHash}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "image/jpeg" },
          body: f.crop,
        });
        if (!up.ok) continue;
        const { key } = (await up.json()) as { key: string };
        await api.addCrop(store.libraryId, {
          id: crypto.randomUUID(),
          identityId,
          cropKey: key,
          quality: f.score,
          sourceAsset: f.assetId,
        });
      } catch {
        // A missing crop costs a face on a card, not the pass.
      }
    }
  }

  // Written per photograph at the end, so a face that turned out to belong to
  // a group is stored with its group already on it.
  const byAsset = new Map<string, Found[]>();
  for (const f of found) {
    const list = byAsset.get(f.assetId) ?? [];
    list.push(f);
    byAsset.set(f.assetId, list);
  }
  // Every photograph the detector ran on, not every photograph with a face in
  // it. An empty write is how "looked at and holds nobody" gets recorded, and
  // without it a landscape is re-decoded and re-detected on every run.
  for (const assetId of examined) {
    const faces = byAsset.get(assetId) ?? [];
    const labels: FaceLabel[] = faces.map((f) => ({
      id: f.id,
      bbox: f.bbox,
      identityId: f.identityId,
      detScore: f.score,
      // The detector reports confidence, not capture quality. Score stands in
      // as an ordering and should not be read as the pipeline's own measure.
      quality: f.score,
      similarity: f.similarity,
      // Never `confirmed`. A machine match is a suggestion; the exemplar
      // policy rests on only human-vouched faces becoming evidence.
      state: f.identityId ? "auto" : "suggested",
    }));
    try {
      await api.putFaces(store.libraryId, assetId, labels);
    } catch {
      // Next run picks the photograph up again; nothing is lost but time.
    }
  }

  // Anything the regrouping just emptied.
  //
  // A face that belonged to a group last run can be recognised as somebody
  // named this run, which leaves the group it came from holding nothing. On
  // the People screen that is a person with no face, no name and no
  // photographs, and there is nothing the user can do with one.
  try {
    await api.reap(store.libraryId);
  } catch {
    // Tidying. Not worth failing a pass over.
  }

  // Descriptions and the date check are the server's, and are queued rather
  // than waited for: they take a vision model minutes per batch and nobody
  // should watch a progress bar for that.
  try {
    await api.queueAnalysis(
      store.libraryId,
      pending.items.map((c) => c.id),
    );
    p.stage = "queued";
  } catch {
    p.stage = "done";
  }
  p.note = undefined;
  say();
  return p;
}
